import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Input,
  Select,
  Button,
  Dropdown,
  Menu,
  Modal,
  Form,
  Radio,
  Checkbox,
  message,
  Spin,
  Tabs,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  UsergroupAddOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  EditOutlined,
  SafetyCertificateOutlined,
  MoreOutlined,
  SendOutlined,
  CopyOutlined,
  SafetyOutlined,
  TeamOutlined,
  SettingOutlined,
  TableOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import {
  propertyService,
  userService,
  permissionService,
  propertyRoleService,
} from '@iso/services';
import { contractorService } from '@iso/services/contractor.service';
import UserAccessWrapper, { ModalContent } from './UserAccess.styles';
import {
  sortRoles,
  getRoleDescription,
  CAP_META,
  MODULE_GROUPS,
  getCapability,
} from './roleDefinitions';

const { Option } = Select;

const DEFAULT_SETTINGS_VALUES = {
  approvalMode: 'manual',
  automaticApprovalMode: 'allEmails',
  domains: '',
  emails: '',
};

const AVATAR_COLORS = [
  '#0f766e', '#b45309', '#1d4ed8', '#0b2a1b',
  '#7c3aed', '#be185d', '#0e7490', '#15803d',
];

const ROLE_COLORS = {
  owner: { bg: '#ecfdf3', color: '#067647' },
  admin: { bg: '#ecfdf3', color: '#067647' },
  manager: { bg: '#eff8ff', color: '#175cd3' },
  modify: { bg: '#eef4ff', color: '#3538cd' },
  worker: { bg: '#eef4ff', color: '#3538cd' },
  view: { bg: '#f8f9fc', color: '#363f72' },
  contractor: { bg: '#f4f3ff', color: '#5925dc' },
  safety: { bg: '#fff4ed', color: '#b93815' },
};

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const avatarColor = (key = '') => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const isUserDeactivated = (user = {}) => {
  if (user.is_active === undefined || user.is_active === null) return false;
  return Number(user.is_active) === 0 || user.is_active === false;
};

const userStatusInfo = (user = {}) => {
  if (isUserDeactivated(user)) return { className: 'deactivated', label: 'Deactivated' };
  if (user.status) return { className: 'active', label: 'Active' };
  if (user.access_requested) return { className: 'pending', label: 'Requested' };
  return { className: 'pending', label: 'Pending' };
};

const roleBadgeStyle = (slug = '') => {
  const key = Object.keys(ROLE_COLORS).find(k => String(slug).toLowerCase().includes(k));
  const palette = ROLE_COLORS[key] || { bg: '#f2f4f7', color: '#475467' };
  return { background: palette.bg, color: palette.color };
};

const UserAccess = () => {
  const history = useHistory();
  const reduxProperty = useSelector((state) => state.property.currentProperty);
  const reduxPermissions = useSelector((state) => state.permission.permissions);

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [canManage, setCanManage] = useState(false);

  const [activeTab, setActiveTab] = useState('users');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  // Contractors tab has its own search/filters — see filteredContractors.
  const [contractorSearch, setContractorSearch] = useState('');
  const [contractorStatusFilter, setContractorStatusFilter] = useState(null);
  const [contractorSourceFilter, setContractorSourceFilter] = useState(null);
  const [contractorBusinessFilter, setContractorBusinessFilter] = useState(null);

  // Safety roles (drives Manage Access safety toggles + "overrides"/"Custom")
  const [roles, setRoles] = useState([]);
  const [roleTypes, setRoleTypes] = useState([]);

  // Reference permission matrix
  const [matrixVisible, setMatrixVisible] = useState(false);

  // Roles tab: per-row "show users with this role" expansion
  const [expandedRoles, setExpandedRoles] = useState(() => new Set());
  const toggleRoleExpanded = useCallback((slug) => {
    setExpandedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  }, []);

  // Contractor approval settings
  const [settingsForm] = Form.useForm();
  const [settingsFetching, setSettingsFetching] = useState(false);
  const [settingsSubmitting, setSettingsSubmitting] = useState(false);
  const approvalMode = Form.useWatch('approvalMode', settingsForm);
  const automaticApprovalMode = Form.useWatch('automaticApprovalMode', settingsForm);

  // Invite modal
  const [inviteVisible, setInviteVisible] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteForm] = Form.useForm();

  // Contractor businesses on this property, for the assign picker in Manage
  // access. Owner/admin-only endpoint, so a non-manager simply gets an empty list
  // and the control stays hidden for them.
  const [businesses, setBusinesses] = useState([]);
  // Which row is mid-assign, so that select alone shows a spinner rather than the
  // whole table blanking.
  const [assigningUserId, setAssigningUserId] = useState(null);

  // Manage access modal
  const [manageUser, setManageUser] = useState(null);
  const [manageSaving, setManageSaving] = useState(false);

  const propertyId = property?.id;

  useEffect(() => {
    const stored = localStorage.getItem('active_property');
    const active = reduxProperty || (stored ? JSON.parse(stored) : null);
    if (active && active.id) {
      setProperty(active);
    } else {
      setLoading(false);
      message.error('No property selected. Please select a property first.');
    }
  }, [reduxProperty]);

  // NOTE: the property/permission/role services in @iso/services normalise the
  // API response to the legacy `{ code: '0000', ... }` envelope (they return
  // `res.data`, not the newer `{ data: { success } }` shape). Success is
  // therefore checked via `res.code === '0000'` consistently below.
  const loadUsers = useCallback(() => {
    if (!propertyId) return Promise.resolve();
    return propertyService.getListUsersOfProperty(propertyId, true).then((res) => {
      if (res.code === '0000' && Array.isArray(res.users)) {
        setUsers(res.users);
      }
    }).finally(() => setLoading(false));
  }, [propertyId]);

  const loadRoles = useCallback(() => {
    if (!propertyId) return Promise.resolve();
    return Promise.all([
      propertyRoleService.getAllRoles(propertyId),
      propertyRoleService.getRoleTypes(),
    ]).then(([rolesRes, typesRes]) => {
      const roleList = rolesRes && rolesRes.code === '0000' ? (rolesRes.roles || []) : [];
      const typeList = typesRes && typesRes.code === '0000' ? (typesRes.role_types || []) : [];
      setRoles(roleList);
      setRoleTypes(typeList);
    }).catch(() => {
      // Roles are optional context; failure shouldn't block the page.
    });
  }, [propertyId]);

  const fetchPropertySettings = useCallback(() => {
    if (!propertyId) {
      settingsForm.setFieldsValue(DEFAULT_SETTINGS_VALUES);
      return;
    }
    setSettingsFetching(true);
    propertyService.getPropertySetting(propertyId).then((res) => {
      if (res.code === '0000' && res.property_setting) {
        const setting = res.property_setting;
        const domains = Array.isArray(setting.domains)
          ? setting.domains.map((item) => (typeof item === 'string' ? item : item.domain)).filter(Boolean)
          : [];
        const emails = Array.isArray(setting.emails)
          ? setting.emails.map((item) => (typeof item === 'string' ? item : item.email)).filter(Boolean)
          : [];
        const autoEnabled = !!setting.enable_auto_contractor_approval;
        let autoModeValue = 'allEmails';
        if (autoEnabled) {
          if (setting.enable_listed_domains) autoModeValue = 'listedDomains';
          else if (setting.enable_listed_emails) autoModeValue = 'listedEmails';
        }
        const approvalModeValue = autoEnabled
          ? 'auto'
          : (setting.reject_contractor_requests ? 'reject' : 'manual');
        settingsForm.setFieldsValue({
          ...DEFAULT_SETTINGS_VALUES,
          approvalMode: approvalModeValue,
          automaticApprovalMode: autoModeValue,
          domains: domains.join('\n'),
          emails: emails.join('\n'),
        });
      } else {
        settingsForm.setFieldsValue(DEFAULT_SETTINGS_VALUES);
      }
    }).finally(() => setSettingsFetching(false));
  }, [propertyId, settingsForm]);

  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    loadUsers();
    loadRoles();
    fetchPropertySettings();
    contractorService.businessOptions(propertyId).then((res) => {
      if (res && res.code === '0000') setBusinesses(res.businesses || []);
    }).catch(() => {
      // Optional context: without it the assign control is hidden, which is the
      // right failure mode on a screen whose job is access, not contractors.
    });
    permissionService.getUserPermission(propertyId).then((res) => {
      if (res && res.code === '0000') {
        setCanManage(res.permission === 'owner' || res.permission === 'admin');
      } else {
        setCanManage(false);
      }
    }).catch(() => {
      // On failure, fail closed (no manage rights) and let the user know the
      // check didn't complete rather than silently hiding admin controls.
      setCanManage(false);
      message.error('Could not verify your access level. Some actions may be hidden.');
    });
  }, [propertyId, loadUsers, loadRoles, fetchPropertySettings]);

  // Permissions list for invite/role selects — prefer redux, fall back to API.
  useEffect(() => {
    if (Array.isArray(reduxPermissions) && reduxPermissions.length) {
      setPermissions(reduxPermissions);
    } else {
      permissionService.getPermissions().then((res) => {
        if (res && Array.isArray(res.permissions)) setPermissions(res.permissions);
      }).catch(() => {});
    }
  }, [reduxPermissions]);

  const contractors = useMemo(
    () => users.filter((u) => u.permission_slug === 'contractor'),
    [users],
  );

  const overrideUserIds = useMemo(() => {
    const set = new Set();
    roles.forEach((role) => (role.users || []).forEach((u) => set.add(u.id)));
    return set;
  }, [roles]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.status && !isUserDeactivated(u)).length,
    pending: users.filter((u) => !u.status && !isUserDeactivated(u)).length,
    deactivated: users.filter((u) => isUserDeactivated(u)).length,
    overrides: overrideUserIds.size,
  }), [users, overrideUserIds]);

  const eligibleManagers = useCallback((excludeUserId) => (
    users.filter((u) => u.id !== excludeUserId && u.status && !isUserDeactivated(u) && u.permission_slug !== 'contractor')
  ), [users]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((u) => {
      if (term && !(`${u.name || ''} ${u.email || ''}`.toLowerCase().includes(term))) return false;
      if (roleFilter && u.permission_slug !== roleFilter) return false;
      const deactivated = isUserDeactivated(u);
      if (statusFilter === 'active' && (!u.status || deactivated)) return false;
      if (statusFilter === 'pending' && (u.status || deactivated)) return false;
      if (statusFilter === 'deactivated' && !deactivated) return false;
      return true;
    });
  }, [users, search, roleFilter, statusFilter]);

  /* ---------- Mutations (preserve modal functionality) ---------- */

  // Batched save from the Manage Access modal: standard role, manager, and
  // safety-role membership changes are persisted together, then data reloaded.
  /**
   * Assign a contractor user to a business. Only reachable when they have none —
   * the API refuses an overwrite, and moving somebody between businesses drags
   * their compliance history with them, so it belongs in People → Contractors.
   */
  const handleAssignBusiness = (user, contractorId) => {
    if (!propertyId || !contractorId) return Promise.resolve();
    setAssigningUserId(user.id);
    return contractorService
      .assignUser(propertyId, user.id, contractorId)
      .then((res) => {
        if (res?.code === '0000') {
          message.success(res.message || 'Assigned to business.');
          // Re-read rather than patching locally: the name is resolved
          // server-side from the worker link, and guessing it here would show
          // the wrong thing if the server adopted an existing worker record.
          return loadUsers();
        }
        message.error(res?.message || 'Could not assign this user to the business.');
        return null;
      })
      .catch(() => message.error('Could not assign this user to the business.'))
      .finally(() => setAssigningUserId(null));
  };

  const handleManageSave = ({ user, permissionId, managerId, safetyAdd, safetyRemove }) => {
    if (!propertyId || !user) return;
    const tasks = [];
    const roleChanged = permissionId && permissionId !== user.permission_id;
    if (roleChanged) {
      // permissionService.update resolves with the raw axios response, so the
      // legacy envelope sits at res.data. A refusal (e.g. transferring ownership
      // to a member who has not accepted) comes back HTTP 200 with a non-'0000'
      // code, which would otherwise be reported to the user as a success.
      tasks.push(permissionService.update({ id: user.user_property_id, permission_id: permissionId })
        .then((res) => {
          const envelope = (res && res.data) || {};
          if (envelope.code && envelope.code !== '0000') {
            throw new Error(envelope.message || 'Failed to update the role.');
          }
          return envelope;
        }));
    }
    if ((managerId || null) !== (user.manager_id || null)) {
      tasks.push(propertyService.updateUserManager(user.user_property_id, managerId || null));
    }
    (safetyAdd || []).forEach((rt) => tasks.push(propertyRoleService.addUserToRole(propertyId, rt, user.id)));
    (safetyRemove || []).forEach((rt) => tasks.push(propertyRoleService.removeUserFromRole(propertyId, rt, user.id)));

    if (!tasks.length) {
      setManageUser(null);
      return;
    }
    setManageSaving(true);
    Promise.all(tasks).then(() => {
      message.success('Access updated.');
      setManageUser(null);
      loadUsers();
      loadRoles();
    }).catch((err) => {
      // Keep the modal open on failure so the change isn't silently lost.
      message.error((err && err.message) || 'Failed to update access.');
      loadUsers();
    }).finally(() => setManageSaving(false));
  };

  // Inline manager change from the Users table column (applied immediately).
  const handleManagerChange = (managerId, object) => {
    const managerName = managerId ? (users.find((u) => u.id === managerId)?.name || null) : null;
    setUsers((prev) => prev.map((u) => (u.id === object.id
      ? { ...u, manager_id: managerId || null, manager_name: managerName }
      : u)));
    propertyService.updateUserManager(object.user_property_id, managerId || null).then((res) => {
      if (res.code === '0000') {
        message.success('Manager updated successfully.');
      } else {
        message.error(res.message || 'Failed to update manager.');
        loadUsers();
      }
    }).catch(() => {
      message.error('Failed to update manager.');
      loadUsers();
    });
  };

  // Also accepts a user's own "join this property" request: the endpoint just
  // clears the pending membership, whatever its role.
  const handleApproveContractor = (object) => {
    if (!propertyId) return;
    setLoading(true);
    propertyService.approveContractor(propertyId, object.id).then((res) => {
      if (res.code === '0000') {
        message.success(object.access_requested ? 'Access request accepted.' : 'Contractor approved.');
        return loadUsers();
      }
      return Promise.reject(new Error(res.message || 'Failed to approve contractor.'));
    }).catch((error) => {
      message.error(error.message || 'Failed to approve contractor.');
    }).finally(() => setLoading(false));
  };

  const handleCancelInvite = (object) => {
    if (!propertyId) return;
    setLoading(true);
    propertyService.cancelInviteUser(propertyId, object.id).then((res) => {
      if (res.code === '0000') {
        message.success(object.access_requested ? 'Request declined.' : 'Invitation updated.');
        return loadUsers();
      }
      return Promise.reject(new Error(res.message || 'Failed to update invitation.'));
    }).catch((error) => {
      message.error(error.message || 'Failed to update invitation.');
    }).finally(() => setLoading(false));
  };

  const resendInvitation = (user) => {
    propertyService.inviteUser(propertyId, {
      permission: user.permission_slug,
      user_email: user.email,
      user_name: user.name,
      is_resend: true,
    }).then((res) => {
      if (res.code === '0000') {
        message.success('Invitation resent.');
        loadUsers();
      } else {
        message.error(res.message || 'Failed to resend invitation.');
      }
    }).catch(() => {
      message.error('Failed to resend invitation.');
    });
  };

  const copyInviteLink = (user) => {
    if (user.invitation_link) {
      navigator.clipboard.writeText(user.invitation_link);
      message.success('Invitation link copied.');
    }
  };

  const handleInviteSubmit = () => {
    inviteForm.validateFields().then((values) => {
      setInviteSubmitting(true);
      propertyService.inviteUser(propertyId, values).then((res) => {
        if (res.code === '0000') {
          message.success('User invited.');
          inviteForm.resetFields();
          setInviteVisible(false);
          loadUsers();
        } else {
          message.error(res.message || 'Failed to invite user.');
        }
      }).finally(() => setInviteSubmitting(false));
    }).catch(() => {});
  };

  const buildSettingsPayload = (values) => {
    const mode = values.approvalMode || 'manual';
    const autoEnabled = mode === 'auto';
    const autoMode = values.automaticApprovalMode || 'allEmails';
    return {
      enable_auto_contractor_approval: autoEnabled,
      enable_all_emails: autoEnabled && autoMode === 'allEmails',
      enable_listed_domains: autoEnabled && autoMode === 'listedDomains',
      enable_listed_emails: autoEnabled && autoMode === 'listedEmails',
      manual_contractor_approval: mode === 'manual',
      reject_contractor_requests: mode === 'reject',
      domains: autoEnabled && autoMode === 'listedDomains' ? (values.domains || '') : '',
      emails: autoEnabled && autoMode === 'listedEmails' ? (values.emails || '') : '',
    };
  };

  const handleSettingsSubmit = () => {
    if (!propertyId) return;
    settingsForm.validateFields().then((values) => {
      setSettingsSubmitting(true);
      propertyService.updatePropertySetting(propertyId, buildSettingsPayload(values)).then((res) => {
        if (res.code === '0000') {
          message.success('Contractor settings updated.');
          fetchPropertySettings();
        } else {
          message.error(res.message || 'Failed to update contractor settings.');
        }
      }).catch(() => {
        message.error('Failed to update contractor settings.');
      }).finally(() => setSettingsSubmitting(false));
    }).catch(() => {});
  };

  /* ---------- Render helpers ---------- */

  const rowMenuItems = (object) => {
    const items = [];
    if (!object.status && (object.access_requested || object.permission_slug === 'contractor')) {
      items.push(
        <Menu.Item key="approve" icon={<CheckCircleFilled />} onClick={() => handleApproveContractor(object)}>
          {object.access_requested ? 'Accept access request' : 'Accept contractor user'}
        </Menu.Item>,
      );
    }
    // A request was never sent as an invitation, so there is no link to resend.
    if (!object.status && !object.access_requested) {
      items.push(
        <Menu.Item key="resend" icon={<SendOutlined />} onClick={() => resendInvitation(object)}>
          Resend invitation
        </Menu.Item>,
        <Menu.Item key="copy" icon={<CopyOutlined />} onClick={() => copyInviteLink(object)}>
          Copy invite link
        </Menu.Item>,
      );
    }
    if (object.permission_id !== 1) {
      items.push(
        <Menu.Item key="remove" danger onClick={() => handleCancelInvite(object)}>
          {object.status ? 'Remove user' : object.access_requested ? 'Decline request' : 'Cancel invite'}
        </Menu.Item>,
      );
    }
    return items;
  };

  const userColumns = [
    {
      title: 'User',
      key: 'user',
      render: (_text, record) => (
        <div className="aa-user-cell">
          <div className="aa-avatar" style={{ background: avatarColor(record.email || record.name) }}>
            {getInitials(record.name)}
          </div>
          <div>
            <div className="aa-user-name">{record.name}</div>
            <div className="aa-user-email">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      render: (_text, record) => (
        <div className="aa-role-cell">
          <span className="aa-badge" style={roleBadgeStyle(record.permission_slug)}>
            {record.permission || record.permission_slug || '—'}
          </span>
          {overrideUserIds.has(record.id) && (
            <Tooltip title="Has one or more safety role assignments">
              <span className="aa-badge aa-badge-custom">
                <SafetyOutlined /> Custom
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_text, record) => {
        const info = userStatusInfo(record);
        const pill = (
          <span className={`aa-status ${info.className}`}>
            <span className="aa-dot" />
            {info.label}
          </span>
        );
        // "Pending" is overloaded across these screens: a contractor profile can be
        // Information Incomplete and a worker's induction/competency can be pending,
        // and neither has anything to do with signing in. Only THIS one gates access,
        // so say which one it is rather than leaving people to guess.
        if (info.className !== 'pending') return pill;
        return (
          <Tooltip title="This user cannot sign in to this property yet. Compliance status (induction, competency) is tracked separately under People → Contractors.">
            {pill}
          </Tooltip>
        );
      },
    },
    {
      title: 'Manager',
      key: 'manager',
      render: (_text, record) => {
        if (!record.status || record.permission_slug === 'contractor') {
          return <span className="aa-muted">—</span>;
        }
        return (
          <Select
            className="aa-manager-select"
            style={{ minWidth: 150 }}
            size="small"
            placeholder="Select manager"
            value={record.manager_id || undefined}
            allowClear
            disabled={!canManage}
            onChange={(value) => handleManagerChange(value, record)}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {eligibleManagers(record.id).map((m) => (
              <Option key={m.id} value={m.id}>{m.name}</Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_text, record) => {
        const menuItems = rowMenuItems(record);
        return (
          <div className="aa-actions-cell">
            <Button className="aa-manage-btn" onClick={() => setManageUser(record)}>
              <SafetyCertificateOutlined /> Manage access
            </Button>
            {canManage && menuItems.length > 0 && (
              <Dropdown overlay={<Menu>{menuItems}</Menu>} trigger={['click']} placement="bottomRight">
                <Button className="aa-kebab" icon={<MoreOutlined />} />
              </Dropdown>
            )}
          </div>
        );
      },
    },
  ];

  // How this contractor reached the property. Both routes land in this one tab and
  // they mean different things to whoever is approving: a property-code request is
  // someone ASKING for access, while a check-in is someone who was already standing
  // on the property — the check-in is recorded either way, and only their AirAgri
  // access is in question. Contractors-tab only; on the Users tab the same flag
  // would read oddly against an admin who happened to fill in the form.
  // Which contractor business this person belongs to on this property. Resolved
  // server-side from the worker->account link, falling back to what they typed at
  // check-in. Blank for anyone who is not a contractor, which is most of the
  // Users tab — hence the muted dash rather than an empty cell.
  const businessNameColumn = {
    title: 'Contractor Business Name',
    key: 'contractor_business_name',
    render: (_text, record) => {
      // Already assigned: show it, and stop. Assignment fills a blank and is not
      // reversible from here — moving somebody carries their induction and
      // competency history with them, which belongs with the worker record under
      // People → Contractors, not an inline dropdown on an access table.
      if (record.contractor_business_name) {
        return record.contractor_business_name;
      }
      // Only contractors have a business, and only a manager can set one.
      if (!canManage || record.permission_slug !== 'contractor') {
        return <span className="aa-muted">—</span>;
      }
      return (
        <Select
          className="aa-manager-select"
          style={{ minWidth: 190 }}
          size="small"
          placeholder="Assign business"
          showSearch
          optionFilterProp="children"
          value={undefined}
          loading={assigningUserId === record.id}
          disabled={assigningUserId === record.id}
          onChange={(value) => handleAssignBusiness(record, value)}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          {businesses.map((b) => (
            <Option key={b.id} value={b.id}>{b.business_name}</Option>
          ))}
        </Select>
      );
    },
  };

  const sourceColumn = {
    title: 'Source',
    key: 'source',
    render: (_text, record) => (
      record.from_checkin ? (
        <Tooltip title="Signed in on this property's contractor check-in form. They have already been on site — the check-in is recorded regardless of this approval.">
          <span className="aa-badge" style={{ background: '#eef4ff', color: '#3538cd' }}>
            Check-in form
          </span>
        </Tooltip>
      ) : (
        <Tooltip title="Requested access using the property code in the AirAgri app.">
          <span className="aa-badge" style={{ background: '#f2f4f7', color: '#475467' }}>
            Property code
          </span>
        </Tooltip>
      )
    ),
  };

  // Users tab keeps its existing columns and gains the business name before
  // Actions. Blank for everyone who is not a contractor, which is most of them.
  const userColumnsWithBusiness = [
    ...userColumns.filter((c) => c.key !== 'actions'),
    businessNameColumn,
    ...userColumns.filter((c) => c.key === 'actions'),
  ];

  const contractorColumns = [
    ...userColumns.filter((c) => c.key !== 'role' && c.key !== 'manager' && c.key !== 'actions'),
    businessNameColumn,
    sourceColumn,
    // Actions stays last — it is right-aligned and reads as the end of the row.
    ...userColumns.filter((c) => c.key === 'actions'),
  ];

  // Contractors get their own search and filters. The Users tab's state is shared
  // across a different column set (it filters on role, which this tab does not
  // show — every row here is a contractor), so reusing it would make one tab's
  // filters silently apply to the other.
  const filteredContractors = useMemo(() => {
    const term = contractorSearch.trim().toLowerCase();
    return contractors.filter((u) => {
      if (term) {
        const haystack = `${u.name || ''} ${u.email || ''} ${u.contractor_business_name || ''}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      const deactivated = isUserDeactivated(u);
      if (contractorStatusFilter === 'active' && (!u.status || deactivated)) return false;
      if (contractorStatusFilter === 'pending' && (u.status || deactivated)) return false;
      if (contractorStatusFilter === 'deactivated' && !deactivated) return false;
      if (contractorSourceFilter === 'checkin' && !u.from_checkin) return false;
      if (contractorSourceFilter === 'code' && u.from_checkin) return false;
      if (contractorBusinessFilter && u.contractor_business_name !== contractorBusinessFilter) return false;
      return true;
    });
  }, [contractors, contractorSearch, contractorStatusFilter, contractorSourceFilter, contractorBusinessFilter]);

  // Businesses actually present in this tab, for the filter dropdown.
  const contractorBusinesses = useMemo(() => {
    const set = new Set();
    contractors.forEach((u) => {
      if (u.contractor_business_name) set.add(u.contractor_business_name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [contractors]);

  const usersTab = (
    <div>
      <div className="aa-filters">
        <Input
          className="aa-search"
          placeholder="Search users..."
          prefix={<SearchOutlined style={{ color: '#98a2b3' }} />}
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FilterOutlined className="aa-filter-icon" />
        <Select
          className="aa-filter-select"
          placeholder="All roles"
          allowClear
          value={roleFilter || undefined}
          onChange={(v) => setRoleFilter(v || null)}
        >
          {permissions.map((p) => (
            <Option key={p.slug} value={p.slug}>{p.name}</Option>
          ))}
        </Select>
        <Select
          className="aa-filter-select"
          placeholder="All statuses"
          allowClear
          value={statusFilter || undefined}
          onChange={(v) => setStatusFilter(v || null)}
        >
          <Option value="active">Active</Option>
          <Option value="pending">Pending</Option>
          <Option value="deactivated">Deactivated</Option>
        </Select>
      </div>
      <Table
        className="aa-table"
        columns={userColumnsWithBusiness}
        dataSource={filteredUsers}
        rowKey={(r) => r.user_property_id || r.id}
        pagination={false}
        locale={{ emptyText: <div className="aa-empty">No users found.</div> }}
      />
    </div>
  );

  const sortedRoles = sortRoles(permissions);

  const rolesTab = (
    <div className="aa-roles">
      <div className="aa-roles-head">
        <div>
          <h3 className="aa-roles-title">Roles</h3>
          <p className="aa-roles-subtitle">Predefined system roles and custom role builder.</p>
        </div>
        <div className="aa-roles-head-actions">
          <span className="aa-phase-pill">Custom Role Builder — Phase 2</span>
          <Button className="aa-matrix-btn" icon={<TableOutlined />} onClick={() => setMatrixVisible(true)}>
            View permission matrix
          </Button>
        </div>
      </div>

      {sortedRoles.length === 0 && (
        <div className="aa-empty">No roles available for this property.</div>
      )}

      {sortedRoles.map((role) => {
        const roleUsers = users.filter((u) => u.permission_slug === role.slug);
        const count = roleUsers.length;
        const isExpanded = expandedRoles.has(role.slug);
        return (
          <div className="aa-role-row-wrap" key={role.id || role.slug}>
            <div className="aa-role-row">
              <div className="aa-role-icon" style={roleBadgeStyle(role.slug)}>
                <SafetyCertificateOutlined />
              </div>
              <div className="aa-role-main">
                <div className="aa-role-name">
                  {role.name}
                  <span className="aa-system-tag">System</span>
                </div>
                <div className="aa-role-desc">{getRoleDescription(role.slug)}</div>
              </div>
              <div className="aa-role-count">
                <span className="n">{count}</span>
                <span className="u">{count === 1 ? 'user' : 'users'}</span>
              </div>
              <Tooltip title={count === 0 ? 'No users with this role' : (isExpanded ? 'Hide users' : 'Show users')}>
                <Button
                  className="aa-role-toggle"
                  type="text"
                  icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                  onClick={() => toggleRoleExpanded(role.slug)}
                  disabled={count === 0}
                  aria-label={isExpanded ? 'Hide users' : 'Show users'}
                />
              </Tooltip>
              <Tooltip title="Custom role builder is coming in Phase 2">
                <Button className="aa-clone-btn" disabled>Clone</Button>
              </Tooltip>
            </div>
            {isExpanded && (
              <div className="aa-role-users">
                {roleUsers.length === 0 ? (
                  <div className="aa-role-users-empty">No users with this role.</div>
                ) : (
                  roleUsers.map((u) => (
                    <div className="aa-role-user" key={u.user_property_id || u.id}>
                      <div className="aa-avatar" style={{ background: avatarColor(u.email || u.name) }}>
                        {getInitials(u.name)}
                      </div>
                      <div className="aa-role-user-meta">
                        <div className="aa-user-name">{u.name}</div>
                        <div className="aa-user-email">{u.email}</div>
                      </div>
                      {(() => {
                        const info = userStatusInfo(u);
                        return (
                          <span className={`aa-status ${info.className}`}>
                            <span className="aa-dot" />
                            {info.label}
                          </span>
                        );
                      })()}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const contractorFilters = (
    <div className="aa-filters">
      <Input
        className="aa-search"
        placeholder="Search contractors by name, email or business..."
        prefix={<SearchOutlined style={{ color: '#98a2b3' }} />}
        allowClear
        value={contractorSearch}
        onChange={(e) => setContractorSearch(e.target.value)}
      />
      <FilterOutlined className="aa-filter-icon" />
      <Select
        className="aa-filter-select"
        placeholder="All businesses"
        allowClear
        showSearch
        value={contractorBusinessFilter || undefined}
        onChange={(v) => setContractorBusinessFilter(v || null)}
      >
        {contractorBusinesses.map((b) => (
          <Option key={b} value={b}>{b}</Option>
        ))}
      </Select>
      <Select
        className="aa-filter-select"
        placeholder="All statuses"
        allowClear
        value={contractorStatusFilter || undefined}
        onChange={(v) => setContractorStatusFilter(v || null)}
      >
        <Option value="active">Active</Option>
        <Option value="pending">Pending</Option>
        <Option value="deactivated">Deactivated</Option>
      </Select>
      <Select
        className="aa-filter-select"
        placeholder="All sources"
        allowClear
        value={contractorSourceFilter || undefined}
        onChange={(v) => setContractorSourceFilter(v || null)}
      >
        <Option value="checkin">Check-in form</Option>
        <Option value="code">Property code</Option>
      </Select>
    </div>
  );

  const contractorTab = loading ? (
    <div className="aa-loading"><Spin /></div>
  ) : (
    <div>
      {contractorFilters}
      <Table
        className="aa-table"
        columns={contractorColumns}
        dataSource={filteredContractors}
        rowKey={(r) => r.user_property_id || r.id}
        pagination={false}
        locale={{
          emptyText: (
            <div className="aa-empty">
              {contractors.length
                ? 'No contractors match these filters.'
                : 'No contractor users found.'}
            </div>
          ),
        }}
      />
    </div>
  );

  const settingsTab = settingsFetching ? (
    <div className="aa-loading"><Spin /></div>
  ) : (
    <div className="aa-settings">
      <Form form={settingsForm} layout="vertical" initialValues={DEFAULT_SETTINGS_VALUES}>
        <Form.Item
          name="approvalMode"
          rules={[{ required: true, message: 'Please select how contractor invitations are handled.' }]}
        >
          <Radio.Group className="settings-radio-group" disabled={!canManage}>
            <Radio value="auto">
              <span>Enable automatic contractor approval</span>
              {approvalMode === 'auto' && (
                <div className="settings-suboptions">
                  <Form.Item
                    name="automaticApprovalMode"
                    rules={[{ required: true, message: 'Please choose how automatic approval is applied.' }]}
                  >
                    <Radio.Group className="settings-radio-group" disabled={!canManage}>
                      <Radio value="allEmails">Enable all emails</Radio>
                      <Radio value="listedDomains">Enable listed domains</Radio>
                      <Radio value="listedEmails">Enable listed emails</Radio>
                    </Radio.Group>
                  </Form.Item>
                  {automaticApprovalMode === 'listedDomains' && (
                    <Form.Item
                      name="domains"
                      label="Allowed domains"
                      rules={[{ required: true, message: 'Please enter at least one domain.' }]}
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder="Enter allowed domains, one per line. Ex: mail.airagri.tech"
                        disabled={!canManage}
                      />
                    </Form.Item>
                  )}
                  {automaticApprovalMode === 'listedEmails' && (
                    <Form.Item
                      name="emails"
                      label="Allowed emails"
                      rules={[{ required: true, message: 'Please enter at least one email.' }]}
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder="Enter allowed emails, one per line. Ex: user-contractor@mail.airagri.tech"
                        disabled={!canManage}
                      />
                    </Form.Item>
                  )}
                </div>
              )}
            </Radio>
            <Radio value="manual">Manual contractor approval</Radio>
            <Radio value="reject">Reject contractor requests</Radio>
          </Radio.Group>
        </Form.Item>
      </Form>
      {canManage && (
        <div className="settings-actions">
          <Button
            className="aa-primary-btn"
            loading={settingsSubmitting}
            onClick={handleSettingsSubmit}
          >
            Update contractor settings
          </Button>
        </div>
      )}
    </div>
  );

  // Order: Users, Contractors, Roles, Settings. Contractors sits second because
  // it is where the day-to-day work is — approving arrivals — while Roles is
  // configuration that is set up once and rarely revisited.
  const tabItems = [
    {
      key: 'users',
      label: <span>Users<span className="aa-tab-count">{stats.total}</span></span>,
      children: usersTab,
    },
    {
      key: 'contractors',
      label: <span>Contractors<span className="aa-tab-count">{contractors.length}</span></span>,
      children: contractorTab,
    },
    {
      key: 'roles',
      label: (
        <span>
          Roles<span className="aa-tab-count">{sortedRoles.length}</span>
        </span>
      ),
      children: rolesTab,
    },
    {
      key: 'settings',
      label: <span><SettingOutlined /> Settings</span>,
      children: settingsTab,
    },
  ];

  const propertyLabel = property
    ? [property.name, property.state || property.region].filter(Boolean).join(' · ')
    : '';

  return (
    <UserAccessWrapper>
      <LayoutWrapper>
        <div className="aa-container">
          <div className="aa-breadcrumb">
            <button type="button" className="aa-breadcrumb-link" onClick={() => history.push('/property-settings')}>
              <SettingOutlined /> Property Settings
            </button>
            <span className="aa-breadcrumb-sep">›</span>
            <span className="aa-breadcrumb-current">User Access Control</span>
            {propertyLabel && <span className="aa-property-chip">{propertyLabel}</span>}
          </div>

          <div className="aa-page-header">
            <div>
              <h1 className="aa-title">User Access Control</h1>
              <p className="aa-subtitle">Manage who can access this property and what they can do.</p>
            </div>
            {canManage && (
              <Button
                type="primary"
                className="aa-invite-btn"
                icon={<UsergroupAddOutlined />}
                onClick={() => setInviteVisible(true)}
              >
                Invite user
              </Button>
            )}
          </div>

          <div className="aa-stats">
            <div className="aa-stat-card brand">
              <div className="aa-stat-icon"><TeamOutlined /></div>
              <div>
                <div className="aa-stat-value">{stats.total}</div>
                <div className="aa-stat-label">Total users</div>
              </div>
            </div>
            <div className="aa-stat-card green">
              <div className="aa-stat-icon"><CheckCircleFilled /></div>
              <div>
                <div className="aa-stat-value">{stats.active}</div>
                <div className="aa-stat-label">Active</div>
              </div>
            </div>
            <div className="aa-stat-card amber">
              <div className="aa-stat-icon"><ClockCircleFilled /></div>
              <div>
                <div className="aa-stat-value">{stats.pending}</div>
                <div className="aa-stat-label">Pending invites</div>
              </div>
            </div>
            <div className="aa-stat-card blue">
              <div className="aa-stat-icon"><EditOutlined /></div>
              <div>
                <div className="aa-stat-value">{stats.overrides}</div>
                <div className="aa-stat-label">With overrides</div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="aa-panel"><div className="aa-loading"><Spin size="large" /></div></div>
          ) : (
            <Tabs
              className="aa-tabs"
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
            />
          )}
        </div>
      </LayoutWrapper>

      <Modal
        title="Invite user"
        open={inviteVisible}
        onCancel={() => { setInviteVisible(false); inviteForm.resetFields(); }}
        onOk={handleInviteSubmit}
        confirmLoading={inviteSubmitting}
        okText="Send invitation"
        okButtonProps={{ style: { background: '#0b2a1b', borderColor: '#0b2a1b' } }}
        destroyOnClose
      >
        <ModalContent>
          <Form form={inviteForm} layout="vertical" name="inviteUser">
            <Form.Item
              name="user_name"
            label="User name"
            rules={[{ required: true, whitespace: true, message: 'This field is required' }]}
          >
            <Input size="large" placeholder="Input user name" />
          </Form.Item>
          <Form.Item
            name="user_email"
            label="User email"
            rules={[
              { required: true, whitespace: true, message: 'This field is required' },
              { type: 'email', message: 'The input is not valid Email' },
              () => ({
                validator(rule, value) {
                  if (value) return userService.checkEmailExistsInProperty(value, propertyId);
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input size="large" placeholder="Input user email" />
          </Form.Item>
          <Form.Item
            name="permission"
            label="Permission"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Select placeholder="Select a permission" allowClear size="large">
              {permissions.map((p) => (
                <Option key={p.id} value={p.slug}>{p.name}</Option>
              ))}
            </Select>
          </Form.Item>
          </Form>
        </ModalContent>
      </Modal>

      <ManageAccessModal
        user={manageUser}
        permissions={permissions}
        businesses={businesses}
        onAssignBusiness={handleAssignBusiness}
        eligibleManagers={manageUser ? eligibleManagers(manageUser.id) : []}
        canManage={canManage}
        roles={roles}
        roleTypes={roleTypes}
        saving={manageSaving}
        onSave={handleManageSave}
        onClose={() => setManageUser(null)}
      />

      <PermissionMatrixModal
        visible={matrixVisible}
        permissions={sortedRoles}
        onClose={() => setMatrixVisible(false)}
      />
    </UserAccessWrapper>
  );
};

const ManageAccessModal = ({
  user, permissions, eligibleManagers, canManage, roles, roleTypes, saving, onSave, onClose,
  businesses = [], onAssignBusiness,
}) => {
  const [roleId, setRoleId] = useState(null);
  const [managerId, setManagerId] = useState(null);
  const [safety, setSafety] = useState(() => new Set());
  const [businessId, setBusinessId] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const currentSafety = useCallback((u) => {
    const set = new Set();
    (roleTypes || []).forEach((rt) => {
      const r = (roles || []).find((x) => x.role_type === rt.value);
      if (r && (r.users || []).some((m) => m.id === u.id)) set.add(rt.value);
    });
    return set;
  }, [roles, roleTypes]);

  useEffect(() => {
    if (!user) return;
    setRoleId(user.permission_id);
    setManagerId(user.manager_id || null);
    setSafety(currentSafety(user));
    setBusinessId(null);
  }, [user, currentSafety]);

  if (!user) return null;

  const selectedSlug = permissions.find((p) => p.id === roleId)?.slug;
  const isOwner = user.permission_id === 1;
  // Ownership can only go to someone who can actually sign in — the server refuses
  // otherwise, because the transfer demotes the current owner immediately and would
  // leave the property with no active owner.
  const isPending = !user.status;
  const isDeactivated = isUserDeactivated(user);
  const cannotBecomeOwner = isPending || isDeactivated;
  // The business picker is for contractors only — it makes no sense for staff, and
  // the column it feeds is blank for them by design.
  const isContractor = user.permission_slug === 'contractor';
  const assignedBusiness = user.contractor_business_name || null;

  const toggleSafety = (value, checked) => {
    setSafety((prev) => {
      const next = new Set(prev);
      if (checked) next.add(value); else next.delete(value);
      return next;
    });
  };

  const handleSave = () => {
    const original = currentSafety(user);
    const safetyAdd = [...safety].filter((v) => !original.has(v));
    const safetyRemove = [...original].filter((v) => !safety.has(v));
    onSave({ user, permissionId: roleId, managerId, safetyAdd, safetyRemove });
  };

  return (
    <Modal
      title="Manage access"
      open={!!user}
      width={560}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        canManage ? (
          <Button
            key="save"
            type="primary"
            loading={saving}
            onClick={handleSave}
            style={{ background: '#0b2a1b', borderColor: '#0b2a1b' }}
          >
            Save changes
          </Button>
        ) : null,
      ]}
    >
      <ModalContent>
        <div className="ma-userhead">
          <div className="ma-avatar">{getInitials(user.name)}</div>
          <div>
            <div className="ma-name">{user.name}</div>
            <div className="ma-email">{user.email}</div>
            {(() => {
              const info = userStatusInfo(user);
              return (
                <span className={`ma-status ${info.className}`}>
                  <span className="dot" />{info.label}
                </span>
              );
            })()}
          </div>
        </div>

        <div className="ma-section-label">Assigned role</div>
        <Select
          style={{ width: '100%' }}
          value={roleId}
          disabled={!canManage || isOwner}
          onChange={setRoleId}
        >
          {permissions.map((p) => (
            <Option key={p.id} value={p.id} disabled={cannotBecomeOwner && p.slug === 'owner'}>{p.name}</Option>
          ))}
        </Select>
        {isOwner && (
          <div className="ma-hint">The property owner role can&apos;t be changed here.</div>
        )}
        {isContractor && (
          <>
            <div className="ma-section-label">Contractor business</div>
            {assignedBusiness ? (
              // Assignment is one-way and only from empty. Moving somebody between
              // businesses carries their compliance history with them, which is a
              // decision for People → Contractors, not a dropdown on an access
              // screen — so once set, this shows the answer and stops.
              <>
                <div className="ma-readonly">{assignedBusiness}</div>
                <div className="ma-hint">
                  To move this person to a different business, edit their worker record under
                  People → Contractors.
                </div>
              </>
            ) : (
              <>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select a contractor business"
                  showSearch
                  optionFilterProp="children"
                  value={businessId || undefined}
                  disabled={!canManage || assigning}
                  onChange={setBusinessId}
                >
                  {businesses.map((b) => (
                    <Option key={b.id} value={b.id}>{b.business_name}</Option>
                  ))}
                </Select>
                <div className="ma-hint">
                  Links this account to a contractor business, so their compliance record and their
                  login are the same person. Create new businesses under People → Contractors.
                </div>
                {canManage && (
                  <Button
                    size="small"
                    style={{ marginTop: 8 }}
                    disabled={!businessId}
                    loading={assigning}
                    onClick={() => {
                      setAssigning(true);
                      Promise.resolve(onAssignBusiness(user, businessId))
                        .finally(() => {
                          setAssigning(false);
                          onClose();
                        });
                    }}
                  >
                    Assign to business
                  </Button>
                )}
              </>
            )}
          </>
        )}

        {!isOwner && isPending && (
          <div className="ma-hint">
            This member hasn&apos;t accepted their invitation yet, so ownership can&apos;t be
            transferred to them. Ask them to accept first, then transfer ownership.
          </div>
        )}
        {!isOwner && !isPending && isDeactivated && (
          <div className="ma-hint">
            This account is deactivated, so ownership can&apos;t be transferred to it.
            Reactivate the account first, then transfer ownership.
          </div>
        )}
        {selectedSlug && <div className="ma-role-desc-box">{getRoleDescription(selectedSlug)}</div>}

        {user.status && user.permission_slug !== 'contractor' && (
          <>
            <div className="ma-section-label">Manager</div>
            <Select
              style={{ width: '100%' }}
              placeholder="Select manager"
              value={managerId || undefined}
              allowClear
              disabled={!canManage}
              onChange={(v) => setManagerId(v || null)}
            >
              {eligibleManagers.map((m) => (
                <Option key={m.id} value={m.id}>{m.name}</Option>
              ))}
            </Select>
          </>
        )}

        <div className="ma-section-label">Safety roles</div>
        {roleTypes.length ? (
          <div className="ma-safety-list">
            {roleTypes.map((rt) => (
              <div className="ma-safety-row" key={rt.value}>
                <span className="ma-safety-label"><SafetyOutlined /> {rt.label}</span>
                <Checkbox
                  checked={safety.has(rt.value)}
                  disabled={!canManage}
                  onChange={(e) => toggleSafety(rt.value, e.target.checked)}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="ma-hint">No safety roles available for this property.</div>
        )}
      </ModalContent>
    </Modal>
  );
};

const PermissionMatrixModal = ({ visible, permissions, onClose }) => (
  <Modal
    title="Permission matrix"
    open={visible}
    width={900}
    onCancel={onClose}
    footer={[<Button key="close" onClick={onClose}>Close</Button>]}
  >
    <ModalContent>
      <div className="pm-legend">
        {Object.keys(CAP_META).map((k) => (
          <span className="pm-legend-item" key={k}>
            <span className="pm-swatch" style={{ background: CAP_META[k].bg }} />
            {CAP_META[k].label === '—' ? 'No access' : CAP_META[k].label}
          </span>
        ))}
      </div>
      <div className="pm-table-wrap">
        <table className="pm-table">
          <thead>
            <tr>
              <th>Module</th>
              {permissions.map((p) => <th key={p.id || p.slug}>{p.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {MODULE_GROUPS.map((grp) => (
              <React.Fragment key={grp.group}>
                <tr className="pm-group">
                  <td colSpan={permissions.length + 1}>{grp.group}</td>
                </tr>
                {grp.modules.map((mod) => (
                  <tr key={mod.key}>
                    <td className="pm-module">{mod.label}</td>
                    {permissions.map((p) => {
                      const meta = CAP_META[getCapability(p.slug, mod.key)];
                      return (
                        <td key={(p.id || p.slug) + mod.key}>
                          <span className="pm-cap" style={{ background: meta.bg, color: meta.color }}>
                            {meta.label}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </ModalContent>
  </Modal>
);

export default UserAccess;
